import * as React from 'react';
import './BuiltInSaveAsModal.css'
import Modal from '@mulesoft/anypoint-components/lib/Modal';
import ModalHeader from '@mulesoft/anypoint-components/lib/ModalHeader';
import ModalBody from '@mulesoft/anypoint-components/lib/ModalBody';
import ModalFooter from '@mulesoft/anypoint-components/lib/ModalFooter';
import Button from '@mulesoft/anypoint-components/lib/Button';
import TextField from '@mulesoft/anypoint-components/lib/TextField'
import IconPanel from '@mulesoft/anypoint-icons/lib/Icon'
import Spinner from '@mulesoft/anypoint-components/lib/Spinner'
import { canBeSlugged } from '../../../../util/Slug'

export type ValueProps = {
  opened: boolean
  nameExists: boolean
  isLoading: boolean
  hasMapPanel: boolean
}

export type EventProps = {
  onClose: () => void
  onApply: (dashboardName: string) => void
}

export type Props = ValueProps & EventProps

export type ModalProps = EventProps & {
  isLoading: boolean,
  hasMapPanel: boolean,
  nameExists: boolean,
  headerText: string,
  cancelButtonText: string,
  confirmButtonText: string,
}

export type ModalState = {
  dashboardName: string,
  isSaveDisabled: boolean,
  isDirty: boolean
}

export class BuiltInSaveAsModal extends React.Component<ModalProps, ModalState> {

  constructor(props: ModalProps) {
    super(props)

    this.state = {
      dashboardName: '',
      isSaveDisabled: true,
      isDirty: false
    }

    this.onDashboardNameChange = this.onDashboardNameChange.bind(this)
    this.onSaveClick = this.onSaveClick.bind(this)
  }

  onDashboardNameChange(value: { value: string }) {
    const isSaveDisabled = !canBeSlugged(value.value)

    this.setState({ dashboardName: value.value, isSaveDisabled: isSaveDisabled, isDirty: true })
  }

  onSaveClick() {
    this.props.onApply(this.state.dashboardName.trim())
  }

  render() {
    const {dashboardName, isSaveDisabled, isDirty} = this.state
    const placeholder = dashboardName || 'Dashboard name'

    return (
      <Modal
        className={`built-in-save-as-modal`}
        onClickOverlay={this.props.onClose}
        testId={`built-in-save-as-modal`}
      >
        <ModalHeader className="built-in-save-as-modal-header" testId={`built-in-save-as-modal-header`} onClose={this.props.onClose}>
          <h1>{this.props.headerText}</h1>
        </ModalHeader>
        <ModalBody className="built-in-save-as-modal-body" testId={`built-in-save-as-modal-body`}>
          <div className="built-in-save-as-selector">
            <p className="built-in-save-as-selector-label">Name</p>
            <div style={{width: '100%'}}>
              <TextField
                className="built-in-save-as-text-field"
                testId="built-in-save-as-text-field"
                name="built-in-save-as-text-field"
                value={dashboardName}
                placeholder={placeholder}
                onChange={this.onDashboardNameChange}
                isDirty={isDirty}
                isValid={!isSaveDisabled}
              />
              {this.props.hasMapPanel && <div className="built-in-save-as-msg info">
                  <IconPanel name={'info-small'} size={14} fill={'#00A2DF'} />
                  <p>This dashboard includes a map panel. Note that map panels are not configurable in custom dashboards.</p>
              </div>
              }
              {this.props.nameExists && <div className="built-in-save-as-msg warning">
                  <IconPanel name={'warning-small'} size={14} fill={'#D1344E'} />
                  <p>A custom dashboard with the same name already exists. Please provide a different name.</p>
              </div>
              }
              {isSaveDisabled && dashboardName !== '' && <div className="built-in-save-as-msg warning">
                  <IconPanel name={'warning-small'} size={14} fill={'#D1344E'} />
                  <p>Dashboard names must contain letters.</p>
              </div>
              }
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="built-in-save-as-modal-footer" testId={`built-in-save-as-modal-footer`}>
          <Button
            className="built-in-save-as-modal-cancel-button"
            testId={`built-in-save-as-modal-cancel-button`}
            kind="tertiary"
            onClick={this.props.onClose}
            noFill={true}
          >
            {this.props.cancelButtonText}
          </Button>
          <Button
            className="built-in-save-as-modal-confirm-button"
            testId={`built-in-save-as-modal-confirm-button`}
            kind="primary"
            onClick={this.onSaveClick}
            disabled={this.state.isSaveDisabled || this.props.isLoading}
          >
            <div className="confirm-button-container">
              <div className="confirm-button-text">
                {this.props.confirmButtonText}
              </div>
              {this.props.isLoading && <Spinner className="confirm-button-spinner" size="s"/>}
            </div>
          </Button>
        </ModalFooter>
      </Modal>
    )
  }
}

export const BuiltInDashboardSaveAsModal = (props: Props) => {
  const { opened, nameExists, isLoading, hasMapPanel } = props

  if (opened) {
    return (
      <BuiltInSaveAsModal
        isLoading={isLoading}
        hasMapPanel={hasMapPanel}
        nameExists={nameExists}
        headerText="Save as"
        cancelButtonText="Cancel"
        confirmButtonText="Save"
        onClose={props.onClose}
        onApply={props.onApply}
      />
    );
  }
  return null;
}
